import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, ChevronLeft, Home } from 'lucide-react';
import { useUIStore } from '../../stores/uiStore';

/**
 * Route → breadcrumb mapping.
 * Each key is a path segment pattern; value is the i18n key.
 */
const ROUTE_LABELS = {
  // Main nav
  dashboard: 'nav.dashboard',
  students: 'nav.studentsList',
  new: 'common.new',
  edit: 'common.edit',
  import: 'common.import',
  financial: 'students.financial',
  onboarding: 'breadcrumbs.onboarding',

  // Students
  attendance: 'nav.attendance',
  promotion: 'breadcrumbs.promotion',
  grades: 'nav.courseGrades',

  // Academics / AI
  academics: 'breadcrumbs.academics',
  'report-comments': 'breadcrumbs.reportComments',
  'fee-reminders': 'breadcrumbs.feeReminders',
  'student-risk': 'breadcrumbs.studentRisk',
  'parent-messages': 'breadcrumbs.parentMessages',

  // Fees
  fees: 'nav.feeSchedules',
  payment: 'nav.recordPayment',
  overdue: 'nav.overdueList',
  invoices: 'nav.invoices',

  // HR
  hr: 'nav.hr',
  employees: 'nav.hrEmployees',
  leave: 'nav.leaveRequests',
  balances: 'nav.leaveBalances',
  absences: 'nav.absences',
  advances: 'nav.advances',
  salaries: 'nav.employeeSalaries',

  // Payroll
  payroll: 'nav.payrollPeriods',

  // Cafeteria
  cafeteria: 'nav.cafeteria',
  items: 'nav.cafeteriaItems',
  sales: 'nav.cafeteriaSales',
  purchases: 'nav.cafeteriaPurchases',
  summary: 'nav.cafeteriaSummary',

  // System
  expenses: 'nav.expenses',
  budgets: 'breadcrumbs.budgets',
  reports: 'nav.reports',
  notifications: 'nav.notifications',

  // Settings
  settings: 'nav.settings',
  school: 'settings.schoolProfileTitle',
  reminders: 'breadcrumbs.reminders',
  invoice: 'breadcrumbs.invoiceSettings',
  'academic-years': 'breadcrumbs.academicYears',
  system: 'breadcrumbs.systemSettings',
  'grading-scales': 'breadcrumbs.gradingScales',
  'audit-log': 'breadcrumbs.auditLog',
  courses: 'breadcrumbs.courses',
  'employee-types': 'breadcrumbs.employeeTypes',
  terms: 'breadcrumbs.terms',
  departments: 'breadcrumbs.departments',
  users: 'breadcrumbs.users',

  // Reports
  'income-statement': 'reports.incomeStatement',
  'balance-sheet': 'reports.balanceSheet',
  'cash-flow': 'reports.cashFlow',
  'vat-report': 'reports.vatReport',
  'daily-collection': 'reports.dailyCollection',
  'overdue-statement': 'reports.overdueStatement',
  'student-roster': 'reports.studentRoster',
  'payroll-summary': 'reports.payrollSummary',
  'attendance-report': 'reports.attendanceReport',
};

/**
 * Auto-generated breadcrumb navigation based on current route.
 * Renders a Home icon + path segments as clickable links.
 */
export default function Breadcrumbs() {
  const { t } = useTranslation();
  const location = useLocation();
  const direction = useUIStore((s) => s.direction);

  const Chevron = direction === 'rtl' ? ChevronLeft : ChevronRight;

  const segments = location.pathname.split('/').filter(Boolean);

  // Don't show breadcrumbs on dashboard (root page)
  if (segments.length <= 1 && segments[0] === 'dashboard') return null;

  const crumbs = segments.map((segment, index) => {
    const path = '/' + segments.slice(0, index + 1).join('/');
    const isLast = index === segments.length - 1;

    // Skip numeric IDs — they are parameters, not navigable labels
    const isId = /^\d+$/.test(segment);
    const labelKey = ROUTE_LABELS[segment];
    const label = isId ? `#${segment}` : labelKey ? t(labelKey, segment) : segment;

    return { path, label, isLast, isId };
  });

  return (
    <nav className="flex items-center gap-1 text-sm text-gray-500 mb-4 flex-wrap">
      <Link
        to="/dashboard"
        className="flex items-center gap-1 hover:text-primary-600 transition-colors"
      >
        <Home size={14} />
      </Link>

      {crumbs.map((crumb, i) => (
        <span key={crumb.path} className="flex items-center gap-1">
          <Chevron size={14} className="text-gray-300" />
          {crumb.isLast ? (
            <span className="font-medium text-gray-900">{crumb.label}</span>
          ) : (
            <Link
              to={crumb.path}
              className="hover:text-primary-600 transition-colors"
            >
              {crumb.label}
            </Link>
          )}
        </span>
      ))}
    </nav>
  );
}
